import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, ArrowRight } from "lucide-react";

export default function ArticleReader({ article, onClose, onWorkWithMe }) {
  useEffect(() => {
    if (!article) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [article, onClose]);

  return (
    <AnimatePresence>
      {article && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] bg-[#FAF7F0] overflow-y-auto"
          data-testid="article-reader"
        >
          <div className="sticky top-0 z-10 backdrop-blur-xl bg-[#FAF7F0]/85 border-b border-[#2F3B2E]/10">
            <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img src="/logo.jpeg" alt="Mended Alchemy" className="h-9 w-9 rounded-full" />
                <span className="font-serif-display text-lg font-medium text-[#2F3B2E]">Mended Alchemy</span>
              </div>
              <button
                onClick={onClose}
                data-testid="article-close"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#2F3B2E]/20 px-4 py-2 text-sm text-[#2F3B2E] hover:bg-[#2F3B2E] hover:text-white transition-colors duration-200"
              >
                <X size={15} /> Close
              </button>
            </div>
          </div>

          <motion.article
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 lg:pt-16 pb-20"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-[#E8E2D5] px-3 py-1 text-[11px] font-medium text-[#2F3B2E]">{article.category}</span>
              <span className="flex items-center gap-1 text-xs text-[#6B665C]"><Clock size={13} /> {article.readTime}</span>
              <span className="font-mono-accent text-[11px] uppercase tracking-wider text-[#6B665C]">{article.date}</span>
            </div>

            <h1 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.12] text-[#3B352D]" data-testid="article-title">
              {article.title}
            </h1>
            <p className="mt-5 font-serif-display text-lg sm:text-xl italic text-[#6B665C] leading-relaxed">{article.excerpt}</p>

            {article.img && (
              <div className="mt-9 overflow-hidden rounded-3xl border border-[#2F3B2E]/12 shadow-[0_25px_50px_-20px_rgba(47,59,46,0.3)]">
                <img src={article.img} alt={article.title} className="w-full h-64 sm:h-80 object-cover" />
              </div>
            )}

            <div className="mt-10 space-y-6" data-testid="article-body">
              {article.body.map((b, i) => {
                if (b.type === "h2") {
                  return (
                    <h2 key={i} className="pt-4 font-serif-display text-2xl sm:text-3xl font-medium text-[#2F3B2E] tracking-tight">
                      {b.text}
                    </h2>
                  );
                }
                if (b.type === "quote") {
                  return (
                    <blockquote key={i} className="my-8 border-l-2 border-[#A8916B] pl-6 py-1">
                      <p className="font-serif-display text-xl sm:text-2xl italic leading-relaxed text-[#2F3B2E]">"{b.text}"</p>
                    </blockquote>
                  );
                }
                return (
                  <p key={i} className="text-base sm:text-lg leading-[1.85] text-[#3B352D]/85">
                    {b.text}
                  </p>
                );
              })}
            </div>

            <p className="mt-12 text-xs italic text-[#6B665C] border-t border-[#2F3B2E]/10 pt-5">{article.source}</p>

            <div className="mt-10 rounded-3xl bg-[#2F3B2E] px-7 py-9 sm:px-10 text-center">
              <p className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-[#C9B48A]">Mended Alchemy · Parenting Coaching</p>
              <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl font-medium text-white">Want guidance tailored to your family?</h3>
              <p className="mt-3 text-sm text-white/75 max-w-md mx-auto leading-relaxed">
                Articles start the journey — a private session changes it. Work 1:1 with Priya on exactly what your home needs.
              </p>
              <button
                onClick={onWorkWithMe}
                data-testid="article-cta"
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-[#A8916B] px-7 py-3.5 text-sm font-medium text-white hover:bg-[#8A7656] transition-colors duration-200"
              >
                Work With Me
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
            </div>
          </motion.article>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
